import { FC, useEffect, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
  productTypeOptions,
} from '@features/admin/reports/reportData';
import {
  ReportPeriod,
  ReportGroupBy,
  TReportPaymentsByProductsBreakdownData,
} from '@src/types/reports';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { useReportChartData, getChartColor } from '@hooks/useReportChartData';
import { useProducts } from '@src/hooks/useProducts';
import { useDisplayedProductsForTableAndChart } from '@src/hooks/useDisplayedProductsForTableAndChart';
import { buildCommonPayloadWithGroupingAndProducts } from '@src/utils/buildCommonPayload';
import dayjs from 'dayjs';
import {
  exportPaymentsByProductsBreakdown,
  getPaymentsByProductsBreakdown,
} from '@src/services/reports';
import { toast } from 'react-toastify';
import { downloadFile } from '@src/lib/utils';
import { canAccess } from '@src/utils/auth';
import { UserRolePermission } from '@src/types/settings';
import { useAuthStore } from '@src/stores/authStore';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    dataKey: string;
    color: string;
    payload: any;
    name: string;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] flex flex-col gap-y-1 min-w-[200px]"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-[#999999] text-[10px] text-center mb-1">
          {payload[0]?.payload?.date}
        </p>
        <div className="flex flex-col gap-y-0.5">
          {payload.map((entry, index) => (
            <div
              key={`item-${index}`}
              className="flex justify-between items-center gap-x-4"
            >
              <div className="flex items-center gap-x-1.5">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-white text-[11px]">{entry.name}</span>
              </div>
              <span className="text-[#B6D72C] text-[11px] font-medium">
                ${entry.value.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return null;
};

export const PaymentsByProductsBreakdown: FC = () => {
  const { auth } = useAuthStore();
  const [data, setData] = useState<TReportPaymentsByProductsBreakdownData[]>(
    []
  );

  const { products } = useProducts();

  // Use custom hooks for filter management
  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.LAST_7_DAYS,
    defaultGroupBy: ReportGroupBy.DAY,
    enableComparisons: true,
    enableProducts: true,
  });

  const { selectedItemsForDisplay } = useDisplayedProductsForTableAndChart(
    products,
    filters.selectedProducts
  );

  // Use custom hook for table columns generation
  const { columns, tableData, displayItems } = useReportTableColumns({
    data,
    selectedItems: selectedItemsForDisplay,
    itemsField: 'products',
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => item.products[key] || 0,
    getPreviousPeriodField: (item, key) =>
      item.products_previous?.[key] || undefined,
    getPreviousYearField: (item, key) =>
      item.products_previous_year?.[key] || undefined,
    columnTitleFormatter: (key) => {
      const product = productTypeOptions.find((opt) => opt.value === key);

      return product?.label.toUpperCase() || key.toUpperCase();
    },
    valueFormatter: (_, value) => `$${value.toLocaleString()}`,
    includeTotalsRow: true,
  });

  // Use custom hook for chart data
  const chartData = useReportChartData({
    data,
    displayItems,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => item.products[key] || 0,
    getPreviousPeriodField: (item, key) =>
      item.products_previous?.[key] || undefined,
    getPreviousYearField: (item, key) =>
      item.products_previous_year?.[key] || undefined,
  });

  const buildReportPayload = () => {
    const payload = buildCommonPayloadWithGroupingAndProducts(filters);

    return {
      ...payload,
      start_date: filters.startDate
        ? dayjs(filters.startDate).format('YYYY-MM-DD')
        : null,
      end_date: filters.endDate
        ? dayjs(filters.endDate).add(1, 'day').format('YYYY-MM-DD')
        : null,
    };
  };

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      const payload = buildReportPayload();
      const response = await getPaymentsByProductsBreakdown(payload);

      setData(response.data);
      toast.success('Report generated successfully!');
    } catch (error) {
      toast.error('Error fetching report');
      console.error('Error fetching report:', error);
    } finally {
      filters.setIsLoading(false);
    }
  };

  const handleExportReport = async () => {
    try {
      const payload = buildReportPayload();
      const response = await exportPaymentsByProductsBreakdown(payload);

      downloadFile(response.data, 'payments-by-products-breakdown');
      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  };

  useEffect(() => {
    handleRunReport();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Payments by Products Breakdown
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          dateRangeValue={filters.valueStart}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          productOptions={products}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          selectedProducts={filters.selectedProducts}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          showProductsFilter={true}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onDateRangeChange={filters.setValueStart}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onProductsChange={filters.setSelectedProducts}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `$${value.toLocaleString()}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                iconType="line"
                wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
              />

              {/* Main data lines */}
              {displayItems.map((item, index) => {
                const label =
                  productTypeOptions.find((opt) => opt.value === item)?.label ||
                  item;

                return (
                  <Line
                    key={item}
                    activeDot={{ r: 6 }}
                    dataKey={item}
                    dot={{ fill: getChartColor(index), r: 4 }}
                    name={label}
                    stroke={getChartColor(index)}
                    strokeWidth={2}
                    type="monotone"
                  />
                );
              })}

              {/* Previous period lines */}
              {filters.compareToPreviousPeriod &&
                displayItems.map((item, index) => {
                  const label =
                    productTypeOptions.find((opt) => opt.value === item)
                      ?.label || item;

                  return (
                    <Line
                      key={`${item}_previous_period`}
                      activeDot={{ r: 4 }}
                      dataKey={`${label}_previous_period`}
                      dot={{
                        fill: getChartColor(index),
                        r: 3,
                      }}
                      name={`${label} (Previous Period)`}
                      stroke={getChartColor(index)}
                      strokeDasharray="5 5"
                      strokeWidth={1.5}
                      type="monotone"
                    />
                  );
                })}

              {/* Previous year lines */}
              {filters.compareToSamePeriodPreviousYear &&
                displayItems.map((item, index) => {
                  const label =
                    productTypeOptions.find((opt) => opt.value === item)
                      ?.label || item;

                  return (
                    <Line
                      key={`${item}_previous_year`}
                      activeDot={{ r: 4 }}
                      dataKey={`${label}_previous_year`}
                      dot={{
                        fill: getChartColor(index),
                        r: 3,
                      }}
                      name={`${label} (Previous Year)`}
                      stroke={getChartColor(index)}
                      strokeDasharray="2 2"
                      strokeWidth={1.5}
                      type="monotone"
                    />
                  );
                })}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Detailed Data</h2>
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          {canAccess(
            UserRolePermission.EXPORT_REPORTS,
            auth?.user?.permissions
          ) && (
            <div className="flex justify-end">
              <Button
                className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
                variant="outline"
                onClick={handleExportReport}
              >
                Download Excel
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
